import { BiicLogo } from './components/brand/BiicLogo';
import { LaunchSequence } from './components/brand/LaunchSequence';
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from './lib/auth/AuthContext';
import { AppShell } from './components/layout/AppShell';
import { LoginPage } from './pages/auth/LoginPage';

// Unknown path: remember it, then go home. If this bundle is stale (a newer build was
// deployed and the service worker served the old shell), the build watch reloads to the
// remembered path instead of the home page, so a fresh link such as /cia still opens.
function UnknownRoute() {
  const loc = useLocation();
  try { sessionStorage.setItem('kanvara-intended-path', JSON.stringify({ path: loc.pathname + loc.search + loc.hash, at: Date.now() })); } catch { /* storage unavailable */ }
  return <Navigate to="/" replace />;
}
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const ReporterBreakGlassPage = lazy(() => import('./pages/admin/ReporterBreakGlassPage').then(m => ({ default: m.ReporterBreakGlassPage })));

function defaultPathForRole(role: string): string {
  // sars_official has no dashboard access at all — straight to trade
  // intelligence, the one page this role can actually see.
  if (role === 'sars_official') return '/intel';
  // Every other role lands on /dashboard. The RoleDashboard wrapper
  // renders the role-specific dashboard variant from there.
  return '/dashboard';
}

const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ReporterDashboardPage = lazy(() => import('./pages/dashboard/ReporterDashboardPage').then(m => ({ default: m.ReporterDashboardPage })));
const InvestigatorDashboardPage = lazy(() => import('./pages/dashboard/InvestigatorDashboardPage').then(m => ({ default: m.InvestigatorDashboardPage })));

function RoleDashboard() {
  const role = useAuthStore(s => s.user?.role);
  // Reporter keeps the simplified capture-instructions dashboard. Every
  // other role sees the same command-centre dashboard with the full
  // intelligence map, KPI strip, and live feed.
  if (role === 'reporter') return <ReporterDashboardPage />;
  return <DashboardPage />;
}
const CapturePage = lazy(() => import('./pages/capture/CapturePage').then(m => ({ default: m.CapturePage })));
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const HistoryPage = lazy(() => import('./pages/capture/HistoryPage').then(m => ({ default: m.HistoryPage })));
const CaptureDetailPage = lazy(() => import('./pages/capture/CaptureDetailPage').then(m => ({ default: m.CaptureDetailPage })));
const WorklistPage = lazy(() => import('./pages/cases/WorklistPage').then(m => ({ default: m.WorklistPage })));
const CaseDetailPage = lazy(() => import('./pages/cases/CaseDetailPage').then(m => ({ default: m.CaseDetailPage })));
const CaseStatsPage = lazy(() => import('./pages/cases/CaseStatsPage').then(m => ({ default: m.CaseStatsPage })));
const AuditPage = lazy(() => import('./pages/audit/AuditPage').then(m => ({ default: m.AuditPage })));
const AuditDetailPage = lazy(() => import('./pages/audit/AuditDetailPage').then(m => ({ default: m.AuditDetailPage })));
const ReviewQueuePage = lazy(() => import('./pages/dashboard/ReviewQueuePage').then(m => ({ default: m.ReviewQueuePage })));
const RegistryPage = lazy(() => import('./pages/registry/RegistryPage').then(m => ({ default: m.RegistryPage })));
const SupportPage = lazy(() => import('./pages/support/SupportPage').then(m => ({ default: m.SupportPage })));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const UsersPage = lazy(() => import('./pages/admin/UsersPage').then(m => ({ default: m.UsersPage })));
const OrganisationsPage = lazy(() => import('./pages/admin/OrganisationsPage').then(m => ({ default: m.OrganisationsPage })));
const AuditLogPage = lazy(() => import('./pages/admin/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const FeatureFlagsPage = lazy(() => import('./pages/admin/FeatureFlagsPage').then(m => ({ default: m.FeatureFlagsPage })));
const ModulesPage = lazy(() => import('./pages/admin/ModulesPage').then(m => ({ default: m.ModulesPage })));
const WebhooksPage = lazy(() => import('./pages/admin/WebhooksPage').then(m => ({ default: m.WebhooksPage })));
const BillingPage = lazy(() => import('./pages/admin/BillingPage').then(m => ({ default: m.BillingPage })));
const QuotaPage = lazy(() => import('./pages/admin/QuotaPage').then(m => ({ default: m.QuotaPage })));
const POPIAPage = lazy(() => import('./pages/admin/POPIAPage').then(m => ({ default: m.POPIAPage })));
const ConsentLedgerPage = lazy(() => import('./pages/admin/ConsentLedgerPage').then(m => ({ default: m.ConsentLedgerPage })));
const RbacCockpitPage = lazy(() => import('./pages/admin/RbacCockpitPage').then(m => ({ default: m.RbacCockpitPage })));
const DSARPage = lazy(() => import('./pages/admin/DSARPage').then(m => ({ default: m.DSARPage })));
const AuthPolicyPage = lazy(() => import('./pages/admin/AuthPolicyPage').then(m => ({ default: m.AuthPolicyPage })));
const OnboardingPage = lazy(() => import('./pages/admin/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const CaptureRulesPage = lazy(() => import('./pages/admin/CaptureRulesPage').then(m => ({ default: m.CaptureRulesPage })));
const OnboardingWizardPage = lazy(() => import('./pages/onboarding/OnboardingWizardPage').then(m => ({ default: m.OnboardingWizardPage })));
const TenantsPage = lazy(() => import('./pages/admin/TenantsPage').then(m => ({ default: m.TenantsPage })));
const TenantWizardPage = lazy(() => import('./pages/admin/TenantWizardPage').then(m => ({ default: m.TenantWizardPage })));
const TenantDetailPage = lazy(() => import('./pages/admin/TenantDetailPage').then(m => ({ default: m.TenantDetailPage })));
const IntelligencePage = lazy(() => import('./pages/intel/IntelligencePage').then(m => ({ default: m.IntelligencePage })));
const BiicPage = lazy(() => import('./pages/biic/BiicPage').then(m => ({ default: m.BiicPage })));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage').then(m => ({ default: m.ReportsPage })));
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const PatternDetectionPage = lazy(() => import('./pages/analytics/PatternDetectionPage').then(m => ({ default: m.PatternDetectionPage })));
const QuarterlyReportPage = lazy(() => import('./pages/reports/QuarterlyReportPage').then(m => ({ default: m.QuarterlyReportPage })));
const AlertSubscriptionsPage = lazy(() => import('./pages/alerts/AlertSubscriptionsPage').then(m => ({ default: m.AlertSubscriptionsPage })));
const ApiKeysPage = lazy(() => import('./pages/settings/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })));
const OutletsPage = lazy(() => import('./pages/admin/OutletsPage').then(m => ({ default: m.OutletsPage })));
const SecuritySettingsPage = lazy(() => import('./pages/settings/SecuritySettingsPage').then(m => ({ default: m.SecuritySettingsPage })));
const OutletDetailPage = lazy(() => import('./pages/outlets/OutletDetailPage').then(m => ({ default: m.OutletDetailPage })));
const LeaderboardPage = lazy(() => import('./pages/leaderboard/LeaderboardPage').then(m => ({ default: m.LeaderboardPage })));
const AgentProfilePage = lazy(() => import('./pages/agents/AgentProfilePage').then(m => ({ default: m.AgentProfilePage })));
const ExternalPortalPage = lazy(() => import('./pages/external/ExternalPortalPage').then(m => ({ default: m.ExternalPortalPage })));
const ExternalLoginPage = lazy(() => import('./pages/external/ExternalLoginPage').then(m => ({ default: m.ExternalLoginPage })));
const ConsumerReportPage = lazy(() => import('./pages/tipoff/ConsumerReportPage').then(m => ({ default: m.ConsumerReportPage })));
const TipOffPage = lazy(() => import('./pages/tipoff/TipOffPage').then(m => ({ default: m.TipOffPage })));
const PitchDeckPage = lazy(() => import('./pages/pitch/PitchDeckPage').then(m => ({ default: m.PitchDeckPage })));
const CiaDeckPage = lazy(() => import('./pages/cia/CiaDeckPage').then(m => ({ default: m.CiaDeckPage })));
const LegalLibraryPage = lazy(() => import('./pages/legal/LegalLibraryPage').then(m => ({ default: m.LegalLibraryPage })));
const BrandPortalPage = lazy(() => import('./pages/brand/BrandPortalPage').then(m => ({ default: m.BrandPortalPage })));
const CiaDemoPage = lazy(() => import('./pages/cia/CiaDemoPage').then(m => ({ default: m.CiaDemoPage })));
const CaseExportPage = lazy(() => import('./pages/cases/CaseExportPage').then(m => ({ default: m.CaseExportPage })));
const HomePage = lazy(() => import('./pages/home/HomePage').then(m => ({ default: m.HomePage })));
const TipOffsPage = lazy(() => import('./pages/admin/TipOffsPage').then(m => ({ default: m.TipOffsPage })));
const LegalHoldsPage = lazy(() => import('./pages/admin/LegalHoldsPage').then(m => ({ default: m.LegalHoldsPage })));

function Loading() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-knv-amber border-t-transparent rounded-full" />
    </div>
  );
}

function ProtectedRoute() {
  const { isAuthenticated, forcePasswordChange } = useAuthStore();
  const workspaceStatus = useAuthStore(s => s.workspace?.status);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const onSettings = location.pathname === '/settings/security';

  if (forcePasswordChange && !onSettings) {
    return <Navigate to="/settings/security?change-password=required" replace />;
  }

  // A user whose tenant admin has not yet given them a workspace picks their
  // modules and profile before anything else is reachable.
  if (workspaceStatus === 'pending' && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // MFA enrolment is no longer enforced — see commit removing MFA from login.
  const inSetupMode = onSettings && forcePasswordChange;

  if (inSetupMode) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 24px', fontFamily: "'Inter Tight', system-ui, sans-serif" }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40 }}>
          <BiicLogo height={40} />
        </div>
        <div style={{ width: '100%', maxWidth: 520 }}>
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </div>
      </div>
    );
  }

  return (
    <AppShell>
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

function RoleRoot() {
  const { user } = useAuthStore();
  const workspace = useAuthStore(s => s.workspace);
  // A user who has chosen a workspace lands on its composed home (a fleet
  // manager on /fleet, a BIIC analyst on /biic). Falling through to
  // /dashboard would show them a page their role cannot read.
  if (workspace?.status === 'pending') return <Navigate to="/onboarding" replace />;
  if (workspace?.home) return <Navigate to={workspace.home} replace />;
  return <Navigate to={defaultPathForRole(user?.role ?? 'admin')} replace />;
}

export function App() {
  return (
    <>
    <LaunchSequence />
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Suspense fallback={<Loading />}><HomePage /></Suspense>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/external/:caseId/login" element={<Suspense fallback={<Loading />}><ExternalLoginPage /></Suspense>} />
      <Route path="/external/:caseId" element={<Suspense fallback={<Loading />}><ExternalPortalPage /></Suspense>} />
      <Route path="/tipoff" element={<Suspense fallback={<Loading />}><TipOffPage /></Suspense>} />
      <Route path="/report" element={<Suspense fallback={<Loading />}><ConsumerReportPage /></Suspense>} />
      <Route path="/pitch" element={<Suspense fallback={<Loading />}><PitchDeckPage /></Suspense>} />
      <Route path="/cia" element={<Suspense fallback={<Loading />}><CiaDeckPage /></Suspense>} />
      <Route path="/dfsa" element={<Suspense fallback={<Loading />}><CiaDeckPage /></Suspense>} />
      <Route path="/cia-demo" element={<Suspense fallback={<Loading />}><CiaDemoPage /></Suspense>} />
      <Route path="/cases/:id/export" element={<Suspense fallback={<Loading />}><CaseExportPage /></Suspense>} />
      
      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/app" element={<RoleRoot />} />
        <Route path="/dashboard" element={<RoleDashboard />} />
        <Route path="/capture" element={<CapturePage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/captures/:id" element={<CaptureDetailPage />} />
        <Route path="/worklist" element={<WorklistPage />} />
        <Route path="/worklist/stats" element={<CaseStatsPage />} />
        <Route path="/cases/:id" element={<CaseDetailPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/audit/:ref" element={<AuditDetailPage />} />
        <Route path="/legal" element={<LegalLibraryPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/review" element={<ReviewQueuePage />} />
        <Route path="/brand" element={<BrandPortalPage />} />
        <Route path="/registry/*" element={<RegistryPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/quarterly" element={<QuarterlyReportPage />} />
        <Route path="/governance" element={<Navigate to="/worklist" replace />} />
        <Route path="/onboarding" element={<OnboardingWizardPage />} />
        <Route path="/intel/*" element={<IntelligencePage />} />
        <Route path="/biic/*" element={<BiicPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/analytics/patterns" element={<PatternDetectionPage />} />
        <Route path="/alerts" element={<AlertSubscriptionsPage />} />
        <Route path="/settings/api-keys" element={<ApiKeysPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/settings/security" element={<SecuritySettingsPage />} />
        <Route path="/outlets/:id" element={<OutletDetailPage />} />
        <Route path="/agents/:userId" element={<AgentProfilePage />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="organisations" element={<OrganisationsPage />} />
          <Route path="outlets" element={<OutletsPage />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/new" element={<TenantWizardPage />} />
          <Route path="tenants/:id" element={<TenantDetailPage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="flags" element={<FeatureFlagsPage />} />
          <Route path="modules" element={<ModulesPage />} />
          <Route path="webhooks" element={<WebhooksPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="quota" element={<QuotaPage />} />
          <Route path="popia" element={<POPIAPage />} />
          <Route path="consent" element={<ConsentLedgerPage />} />
          <Route path="dsar" element={<DSARPage />} />
          <Route path="auth-policy" element={<AuthPolicyPage />} />
          <Route path="rbac" element={<RbacCockpitPage />} />
          <Route path="onboarding" element={<OnboardingPage />} />
          <Route path="capture-rules" element={<CaptureRulesPage />} />
          <Route path="reporter-tokens" element={<ReporterBreakGlassPage />} />
          <Route path="tipoffs" element={<TipOffsPage />} />
          <Route path="legal-holds" element={<LegalHoldsPage />} />
        </Route>
      </Route>
      
      {/* 404 */}
      <Route path="*" element={<UnknownRoute />} />
    </Routes>
    </>
  );
}
